import type { Client, Product, Sale } from '../types';

// Read the API base URL from Vite's environment variables
// VITE_API_BASE_URL will be 'http://localhost:8080/api' in development (via .env)
// and '/api' in production (so it uses the proxy)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api-otica.leocezar.com.br/api';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }
  return response.json();
}

// --- Client API ---
export const clientApi = {
  async getClients(): Promise<Client[]> {
    const response = await fetch(`${API_BASE_URL}/clients`);
    return handleResponse<Client[]>(response);
  },

  async addClient(client: Omit<Client, 'id'>): Promise<Client> {
    const response = await fetch(`${API_BASE_URL}/clients`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(client),
    });
    return handleResponse<Client>(response);
  },

  async updateClient(updatedClient: Client): Promise<Client> {
    const response = await fetch(`${API_BASE_URL}/clients/${updatedClient.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedClient),
    });
    return handleResponse<Client>(response);
  },

  async deleteClient(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/clients/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  },
};

// --- Inventory API ---
export const inventoryApi = {
  async getProducts(): Promise<Product[]> {
    const response = await fetch(`${API_BASE_URL}/products`);
    return handleResponse<Product[]>(response);
  },

  async addProduct(product: Omit<Product, 'id'>): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(product),
    });
    return handleResponse<Product>(response);
  },

  async updateProduct(updatedProduct: Product): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${updatedProduct.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedProduct),
    });
    return handleResponse<Product>(response);
  },

  async updateProductStock(productId: string, change: number): Promise<Product> {
    const response = await fetch(`${API_BASE_URL}/products/${productId}/stock`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ change }),
    });
    return handleResponse<Product>(response);
  },

  async deleteProduct(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/products/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  },
};

// --- Sales API ---
export const saleApi = {
  async getSales(): Promise<Sale[]> {
    const response = await fetch(`${API_BASE_URL}/sales`);
    const sales = await handleResponse<any[]>(response);
    return sales.map(sale => ({ ...sale, date: new Date(sale.date) }));
  },

  async addSale(sale: Omit<Sale, 'id'>): Promise<Sale> {
    const response = await fetch(`${API_BASE_URL}/sales`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(sale),
    });
    return handleResponse<Sale>(response);
  },

  async getSale(saleId: string): Promise<Sale | undefined> {
    const response = await fetch(`${API_BASE_URL}/sales/${saleId}`);
    const sale = await handleResponse<any>(response);
    return sale ? { ...sale, date: new Date(sale.date) } : undefined;
  },

  async updateSale(updatedSale: Sale): Promise<Sale> {
    const response = await fetch(`${API_BASE_URL}/sales/${updatedSale.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedSale),
    });
    return handleResponse<Sale>(response);
  },

  async deleteSale(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/sales/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }
  },
};
